import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "wanm_consent";
const CONSENT_VERSION = 1;

const defaultConsent = { necessary: true, analytics: false, marketing: false };

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed.choices;
  } catch {
    return null;
  }
}

function writeConsent(choices) {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ version: CONSENT_VERSION, ts: new Date().toISOString(), choices })
  );
}

const ConsentContext = createContext(null);

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(null); // null = not yet decided
  const [decided, setDecided] = useState(false);
  const [prefOpen, setPrefOpen] = useState(false);

  useEffect(() => {
    const saved = readConsent();
    if (saved) {
      setConsent({ ...defaultConsent, ...saved, necessary: true });
      setDecided(true);
    } else {
      setConsent({ ...defaultConsent });
      setDecided(false);
    }
  }, []);

  const persist = useCallback((choices) => {
    const finalChoices = { ...choices, necessary: true };
    writeConsent(finalChoices);
    setConsent(finalChoices);
    setDecided(true);
    setPrefOpen(false);
    // Broadcast so TrackingLoader can react without a full reload
    window.dispatchEvent(new CustomEvent("wanm-consent-change", { detail: finalChoices }));
  }, []);

  const acceptAll = useCallback(() => persist({ analytics: true, marketing: true }), [persist]);
  const rejectAll = useCallback(() => persist({ analytics: false, marketing: false }), [persist]);
  const openPreferences = useCallback(() => setPrefOpen(true), []);
  const closePreferences = useCallback(() => setPrefOpen(false), []);

  return (
    <ConsentContext.Provider
      value={{ consent, decided, prefOpen, acceptAll, rejectAll, savePreferences: persist, openPreferences, closePreferences }}
    >
      {children}
    </ConsentContext.Provider>
  );
}

export const useConsent = () => useContext(ConsentContext);
